import _ from 'lodash';
import { ITransferPrescriptions } from '../types/interfaces/transfer-prescriptions';
import { IMedication } from '../types/interfaces/medication';
import { glpMedications } from '../constants/glp-medications';

// Only Tablet/Capsule get a "- qty type" suffix; everything else just shows a plain quantity.
const QUANTITY_UNIT_SOURCE = String.raw`\b(Tablet|Capsule)s?\b`;
const QUANTITY_UNIT_REGEX = new RegExp(QUANTITY_UNIT_SOURCE, 'i');
const QUANTITY_UNIT_REGEX_GLOBAL = new RegExp(QUANTITY_UNIT_SOURCE, 'gi');

export const extractQuantityUnitFromMedicationName = (name: string): { name: string; unit: string | null } => {
    const match = name.match(QUANTITY_UNIT_REGEX);
    if (!match) return { name, unit: null };

    const strippedName = name.replace(QUANTITY_UNIT_REGEX_GLOBAL, '').replace(/\s{2,}/g, ' ').trim();

    return { name: strippedName, unit: _.capitalize(match[1].toLowerCase()) };
};

const pluralizeUnit = (unit: string, quantity: number) => (quantity === 1 ? unit : `${unit}s`);

export const formatMedicationNameWithQuantity = (name: string, quantity?: number | string | null): string => {
    const { name: strippedName, unit } = extractQuantityUnitFromMedicationName(name);
    const numericQuantity = Number(quantity);

    if (!quantity || Number.isNaN(numericQuantity)) return strippedName;

    return unit ? `${strippedName} - ${numericQuantity} ${pluralizeUnit(unit, numericQuantity)}` : `${strippedName} - ${numericQuantity}`;
};

export const groupPrescrpitionListByProvider = (prescriptions: ITransferPrescriptions[]) => {
    const groupedObj = _.groupBy(prescriptions, item => `${item.provider.npi}_${item.provider.spi}`);

    return Object.values(groupedObj).map(prescList => ({
        provider: prescList[0].provider,
        list: prescList
    }));
};

export const isC2Medication = (medication: ITransferPrescriptions | IMedication) => {
    return (medication as ITransferPrescriptions).prescribable?.deaClass === 'C48675' || (medication as IMedication).deaClass === 'C48675';
};

export const isHigherC2Medication = (medication: ITransferPrescriptions | IMedication) => {
    //C48676 , C48677, C48679
    return ['C48676', 'C48677', 'C48679'].includes(
        (medication as ITransferPrescriptions).prescribable?.deaClass || (medication as IMedication).deaClass
    );
};

export const isGLPMedication = (medication: ITransferPrescriptions | IMedication) =>
    glpMedications.some(
        glpMed =>
            (medication as ITransferPrescriptions).prescribable?.name.toLowerCase().includes(glpMed) ||
            (medication as IMedication).name?.toLowerCase().includes(glpMed)
    );

export const getMedId = (medication: ITransferPrescriptions | IMedication) =>
    (medication as ITransferPrescriptions).prescribable?.medid || medication.medid;

export const formatPrescriptionDetails = (prescription: ITransferPrescriptions): string => {
    const medicationDetails = `
Medication Details:
 Medication Name: ${prescription.prescribable.name}
 DEA Class: ${prescription.prescribable.deaClass}
 Therapeutic Class: ${prescription.prescribable.therapeuticClass}
 OTC: ${prescription.provider ? 'Yes' : 'No'}
`;

    const providerInfo = prescription.provider
        ? `
Provider Information:
 Provider NPI: ${prescription.provider.npi}
 Provider Name: ${prescription.provider.lastName}, ${prescription.provider.firstName}
 Fax: ${prescription.provider.fax || 'N/A'}
 Phone: ${prescription.provider.phoneNumber || 'N/A'}
`
        : 'Provider Information: Not Available\n';

    const fromPharmacyInfo = prescription.fromPharmacy
        ? `
From Pharmacy:
 Pharmacy NPI: ${prescription.fromPharmacy.npi}
 Pharmacy Name: ${prescription.fromPharmacy.name}
 Pharmacy Phone: ${prescription.fromPharmacy.phone}
 Fax: ${prescription.fromPharmacy.fax || 'N/A'}
`
        : 'From Pharmacy: Not Available\n';

    const additionalDetails = `
Additional Details:
 Packaging: ${prescription.packaging || 'N/A'}
 Direction: ${prescription.direction || 'N/A'}
 Quantity: ${prescription.quantity || 'N/A'}
`;

    return (
        medicationDetails +
        providerInfo +
        fromPharmacyInfo +
        (prescription.packaging || prescription.direction || prescription.quantity ? additionalDetails : '')
    );
};
